import { ExternalLink, Star } from 'lucide-react';
import rating from '@/data/google-rating.json';

/**
 * The business's real Google rating (2026-10-14). The numbers come from the
 * Business Profile API: gbp_api.py (facebook-leads, daily cron) rewrites
 * src/data/google-rating.json when the average or the count changes, so the
 * prerendered HTML always carries the current figure. Until 2026-10-14 this
 * showed a fixed "4.9 · 87+" from the bot's feed, which wasn't Google data.
 * Visible only — no aggregateRating in JSON-LD (Google ignores self-served
 * ratings for local businesses).
 */
export const GOOGLE_REVIEWS_URL = rating.mapsUrl;
export const GOOGLE_WRITE_REVIEW_URL = rating.writeReviewUrl;

const hasRating = typeof rating.average === 'number' && typeof rating.total === 'number' && rating.total > 0;
export const ratingText = hasRating ? `${rating.average.toFixed(1)} · ${rating.total} ביקורות בגוגל` : '';

export function Stars({ size = 'h-4 w-4' }: { size?: string }) {
  return (
    <span className="inline-flex items-center" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className={`${size} ${i < Math.round(rating.average) ? 'fill-amber-400 text-amber-400' : 'text-amber-200'}`} />
      ))}
    </span>
  );
}

export default function GoogleRating({ variant = 'inline' }: { variant?: 'inline' | 'pill' }) {
  if (variant === 'pill') {
    return (
      <a
        href={GOOGLE_REVIEWS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-full border border-[var(--cin-line)] bg-white px-3 py-1.5 text-sm shadow-sm hover:border-[var(--cin-blue)]/50 transition"
      >
        {hasRating && <Stars />}
        <span className="font-semibold text-[var(--cin-ink)]">{hasRating ? ratingText : 'מה כתבו עלינו בגוגל'}</span>
        <ExternalLink className="h-3.5 w-3.5 text-[var(--cin-muted)]" aria-hidden="true" />
      </a>
    );
  }

  return (
    <a
      href={GOOGLE_REVIEWS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--cin-ink)] hover:underline decoration-[var(--cin-blue)]/40 underline-offset-4"
    >
      {hasRating && <Stars />}
      {hasRating ? `${ratingText} ←` : 'מה כתבו עלינו בגוגל ←'}
    </a>
  );
}
